import React from 'react';
import { cn } from '../utils/cn';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tailwind size classes (e.g. 'h-4 w-32'). Defaults to a small text-sized block. */
  shape?: 'block' | 'circle' | 'pill';
}

export const Skeleton: React.FC<SkeletonProps> = ({ shape = 'block', className, ...props }) => (
  <div
    className={cn(
      'skeleton',
      shape === 'circle' && 'rounded-full',
      shape === 'pill' && 'rounded-full',
      shape === 'block' && 'rounded-md',
      className,
    )}
    aria-hidden
    {...props}
  />
);

export interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({ lines = 3, className }) => (
  <div className={cn('flex flex-col gap-2', className)} aria-hidden>
    {Array.from({ length: lines }).map((_, idx) => (
      <Skeleton
        key={idx}
        className={cn('h-4', idx === lines - 1 ? 'w-2/3' : 'w-full')}
      />
    ))}
  </div>
);

export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={cn(
      'rounded-md border border-outline/30 bg-surface p-5 shadow-level-1',
      className,
    )}
    aria-busy="true"
  >
    <div className="flex items-center gap-3">
      <Skeleton shape="circle" className="h-11 w-11" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
    <div className="mt-4 space-y-2">
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  </div>
);
